import { useState } from "react";
import { api } from "@/lib/kfmApi";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import CopyButton from "@/components/CopyButton";
import { noteSnippetText, downloadNoteImage } from "@/lib/shareNote";
import { NotebookPen, Check, Image as ImageIcon } from "lucide-react";

// Right-panel notes (desktop): always visible, auto-saves on blur, shareable.
export default function NotesPanel({ session, onNotesChange }) {
  const [notes, setNotes] = useState(session.notes || "");
  const [saved, setSaved] = useState(false);
  const reference = session.study?.passage_reference;

  const save = async () => {
    try {
      await api.put(`/study/sessions/${session.id}/notes`, { text: notes });
      onNotesChange?.(notes);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {}
  };

  return (
    <section data-testid="notes-panel" className="px-6 py-5 space-y-3">
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-slate-500">
          <NotebookPen className="w-4 h-4" strokeWidth={1.5} /> Your notes
        </span>
        <span className="text-[11px] text-slate-500">{notes.length} chars</span>
      </div>
      <Textarea
        data-testid="notes-input"
        value={notes}
        onChange={(e) => { setNotes(e.target.value); onNotesChange?.(e.target.value); }}
        onBlur={save}
        placeholder="Your own observations on this passage…"
        rows={5}
        className="rounded-xl border-[#29344D] resize-none focus-visible:ring-[#00E5FF]/30"
      />
      <div className="flex items-center justify-between">
        <span className="text-xs text-slate-500 flex items-center gap-1">
          {saved && (<><Check className="w-3 h-3 text-[#00E5FF]" strokeWidth={2} /> Saved</>)}
        </span>
        <div className="flex items-center gap-2">
          {notes.trim() && (
            <>
              <CopyButton text={noteSnippetText(reference, notes)} label="Note copied" testid="share-note-copy" />
              <Button
                data-testid="share-note-image"
                onClick={() => downloadNoteImage(reference, notes)}
                variant="outline"
                className="h-9 rounded-full border-[#29344D] text-sm"
              >
                <ImageIcon className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Image
              </Button>
            </>
          )}
          <Button onClick={save} variant="outline" className="h-9 rounded-full border-[#29344D] text-sm">
            Save
          </Button>
        </div>
      </div>
    </section>
  );
}
